import React, { useEffect, useState } from "react";
import IconFreeSpin from "../../mui/JSXIcons/IconFreeSpin";
import { useTranslation } from "react-i18next";
import FreeSpinCardGame from "./free_spin_card__game/FreeSpinCardGame";
import ShowContainer from "../../mui/ShowContainer";
import { ENUM_MODE_GAME, ENUM_USER_ACTIVE, ENUM_WELCOME_USER_STATUS } from "../../../store/enums";
import { useAppSelector } from "../../../store/index.ts";
import useCampaigns from "../../../hooks/other/useCampaigns.js";
import useBonusHelper from "../../../hooks/other/useBonusHelper.js";
import RefuseBonusModal from "../../modal/refuse_bonus/RefuseBonusModal.jsx";
import { API_CASINO } from "../../../api/casino.js";
import useLoginAction from "../../../hooks/useLoginAction.js";
import BonusBtn from "../static_bonus/ui/bonus_btn/BonusBtn.jsx";
import "./free-spin.scss";
import useGameClick from "../../../hooks/other/useGameClick.js";
import { setModalBalance, setModalLogin } from "../../../store/siteSlice.js";
import { useDispatch } from "react-redux";
import ActiveBonus from "./active_bonus/ActiveBonus.jsx";
import { setHardModeGame } from "../../../store/commonSlice.js";

export default function FreeSpin() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getCampaigns } = useCampaigns();
  const { onLoginAction } = useLoginAction();
  const { onGameClick } = useGameClick();

  const currency = useAppSelector((state) => state.user.currency);
  const freeSpins = useAppSelector((state) => state.promotions.freeSpins);
  const welcome = useAppSelector((state) => state.promotions.welcome);
  const isAuth = useAppSelector((state) => state.user.isAuth);

  const [freeSpinsBonuses, setFreeSpinsBonuses] = useState([]);
  const [openRefuse, setOpenRefuse] = useState(false);
  const [alreadyActivated, setAlreadyActivated] = useState(false);
  const [openActiveBonus, setOpenActiveBonus] = useState(false);
  const isWelcomeActive =
    welcome?.statusForUser === ENUM_WELCOME_USER_STATUS.enabled ||
    welcome?.statusForUser === ENUM_WELCOME_USER_STATUS.accrued;

  const plugText = isWelcomeActive
    ? "Free spin bonuses are not available while there is an available Welcome Bonus."
    : "Temporarily unavailable. Reason: Another freespin bonus has been activated.";

  const { btnLoading, setBtnLoading, thenRequest, getSendStatus } = useBonusHelper();

  const onActiveClick = (bonus) => {
    if (bonus.userActivate.status === ENUM_USER_ACTIVE.activated) {
      setOpenRefuse(bonus);
      return;
    }
    onLoginAction(() => changeActive(bonus));
  };

  const changeActive = (bonus) => {
    if (!bonus) return;

    const bonusId = bonus.id;
    const status = getSendStatus(bonus.userActivate.status);

    setBtnLoading(true);

    API_CASINO.bonus
      .setActive({ bonusId, status, currency: currency })
      .then(() => {
        thenRequest(status);
      })
      .catch(console.log);
  };

  useEffect(() => {
    const getNewFreeSpins = async () => {
      const promises = [];
      const newFreeSpins = structuredClone(freeSpins) || [];

      for (let i = 0; i < newFreeSpins?.length; i++) {
        for (let j = 0; j < newFreeSpins[i].campaigns.length; j++) {
          const campaign = newFreeSpins[i].campaigns[j];
          const campaignId = campaign.campaignId;
          const curRes = getCampaigns(campaignId).then((info) => {
            // if (!info?.games) return;
            const games = info?.games?.map((el) => ({
              id: el.gameId,
              image: el.image,
              name: el.name,
              provider: el.provider,
              freespins: el.freespins,
              bet: el.bet || {},
              wager: el.wager,
              cashout: el.cashout
            }));

            if (!newFreeSpins[i].campaigns[j].games) newFreeSpins[i].campaigns[j].games = [];

            newFreeSpins[i].campaigns[j].games = [...newFreeSpins[i].campaigns[j].games, ...games];
          });

          promises.push(curRes);
        }
      }

      Promise.all(promises)
        .then(() => {
          setFreeSpinsBonuses(newFreeSpins);
          setAlreadyActivated(
            newFreeSpins.some((el) => el.userActivate?.status === ENUM_USER_ACTIVE.activated)
          );
        })
        .catch(console.error);
    };

    getNewFreeSpins();
  }, [freeSpins]);

  return (
    <ShowContainer condition={freeSpinsBonuses.length}>
      <article className="free-spin">
        <h3>
          <IconFreeSpin />
          {t("Freespins")}
        </h3>

        <div className="free-spin__content">
          <div className="free-spin__bonuses">
            {freeSpinsBonuses?.map((el) => {
              const userActivate = el?.userActivate || {};
              const userActivateEnable = userActivate?.enabled;
              const userActivateStatus = userActivate?.status;
              const isDisabled = userActivateStatus === ENUM_USER_ACTIVE.disabled;
              const isStatusActivated = userActivateStatus === ENUM_USER_ACTIVE.activated;
              const maxCampaignDeposit = el.campaigns?.map((item) => item.maxDeposit) || [];
              const maxDeposit = Math.max(...maxCampaignDeposit);
              const statusForUser = el?.statusForUser;

              const bonusAccrued = statusForUser == ENUM_USER_ACTIVE.accrued;
              const statusForUserDisabled = statusForUser === ENUM_USER_ACTIVE.disabled;
              const availableTerms = !isWelcomeActive && (!alreadyActivated || isStatusActivated);

              return (
                <div key={el.id} className="free-spin__bonus">
                  <h4>{el.name}</h4>
                  <div className="free-spin__bonus__campaigns">
                    {el.campaigns?.map((campaign) => (
                      <div key={campaign.id} className="free-spin__bonus__campaign">
                        <ShowContainer condition={campaign.minDeposit || campaign.maxDeposit}>
                          <div className="free-spin__bonus__campaign__info">
                            <p>
                              {campaign.maxDeposit
                                ? t("For deposit from", {
                                    minDeposit: campaign.minDeposit || 0,
                                    maxDeposit: campaign.maxDeposit,
                                    currency
                                  })
                                : t("For deposit only from", {
                                    minDeposit: campaign.minDeposit || 0,
                                    currency
                                  })}
                            </p>
                          </div>
                        </ShowContainer>
                        <div className="free-spin__bonus__games">
                          {campaign.games?.map((game) => {
                            const maxCampaignDep = campaign.maxDeposit;
                            const onRunGame = () => {
                              dispatch(setHardModeGame(ENUM_MODE_GAME.real));
                              onGameClick({ currentGame: game, url: "/game/", isChoose: false });
                            };

                            const getOnPlayBtn = () => {
                              if (!isAuth) return () => dispatch(setModalLogin(true));
                              if (!availableTerms) return null;
                              if (!userActivateEnable) return null;
                              if (bonusAccrued) return onRunGame;
                              if (isStatusActivated && maxCampaignDep) {
                                return () => dispatch(setModalBalance(maxCampaignDep));
                              }
                              if (!isStatusActivated) return () => setOpenActiveBonus(el);
                              return onRunGame;
                            };

                            return (
                              <FreeSpinCardGame
                                key={game.id}
                                game={game}
                                onPlayBtn={getOnPlayBtn()}
                              />
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="free-spin__terms">
                    <ShowContainer
                      condition={availableTerms}
                      plug={
                        <div className="free-spin__info__terms">
                          <p>{t(plugText)}</p>
                        </div>
                      }>
                      <ShowContainer condition={el.terms}>
                        <div className="free-spin__info__terms">
                          <h5>{t("Conditions for receiving:")}</h5>
                          <p>{el.terms}</p>
                        </div>
                      </ShowContainer>

                      <BonusBtn
                        showBtnActive={userActivateEnable}
                        disable={btnLoading || isDisabled || statusForUserDisabled}
                        activeTo={el.expired}
                        activateStatus={userActivateStatus}
                        statusForUser={statusForUser}
                        onClick={() => onActiveClick(el)}
                        deposit={maxDeposit}
                      />
                    </ShowContainer>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </article>

      <RefuseBonusModal
        isOpen={openRefuse}
        closeModal={() => setOpenRefuse(false)}
        bonusName={t("Freespin bonus`a")}
        onConfirm={() => {
          changeActive(openRefuse);
          setOpenRefuse(false);
        }}
      />

      <ActiveBonus
        isOpen={!!openActiveBonus}
        closeModal={() => setOpenActiveBonus(false)}
        bonus={openActiveBonus}
        onConfirm={() => {
          setOpenActiveBonus(false);
          onLoginAction(() => changeActive(openActiveBonus));
        }}
      />
    </ShowContainer>
  );
}
